import { useAuth } from "@/context/AuthContext";
import {
  completeUpload,
  deleteDocument,
  downloadDocument,
  getDocumentById,
  getDocumentViewUrl,
  listDocuments,
  type Document,
  type DocumentFilters,
} from "@/services/documentsApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchAuthSession } from "aws-amplify/auth";

/**
 * Hook to get access token for API calls
 */
async function getAccessToken(): Promise<string> {
  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken?.toString();
  if (!accessToken) {
    throw new Error("No access token available");
  }
  return accessToken;
}

/**
 * Hook to fetch list of documents with polling and filters
 */
export function useDocuments(pollingInterval?: number, filters?: DocumentFilters) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ["documents", filters],
    queryFn: async () => {
      const token = await getAccessToken();
      return listDocuments(token, filters);
    },
    enabled: isAuthenticated,
    refetchInterval: pollingInterval, // Poll for status updates
    staleTime: 10000, // Consider data stale after 10 seconds
  });
}

/**
 * Hook to fetch a single document by ID
 */
export function useDocument(documentId: string | undefined) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ["document", documentId],
    queryFn: async () => {
      if (!documentId) throw new Error("Document ID is required");
      const token = await getAccessToken();
      return getDocumentById(documentId, token);
    },
    enabled: isAuthenticated && !!documentId,
  });
}

/**
 * Hook to upload a document with progress tracking
 */
export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      documentType = "UNKNOWN",
      onProgress,
    }: {
      file: File;
      documentType?: string;
      onProgress?: (progress: number) => void;
    }) => {
      const token = await getAccessToken();
      return completeUpload(file, token, documentType, onProgress);
    },
    onSuccess: (newDocument) => {
      // Invalidate and refetch documents list
      queryClient.invalidateQueries({ queryKey: ["documents"] });

      // Add the new document to the cache
      queryClient.setQueryData<Document[]>(["documents"], (old) => {
        if (!old) return [newDocument];
        return [newDocument, ...old];
      });
    },
  });
}

/**
 * Hook to get document view URL (presigned URL for viewing in browser)
 */
export function useDocumentViewUrl(documentId: string | undefined) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ["documentViewUrl", documentId],
    queryFn: async () => {
      if (!documentId) throw new Error("Document ID is required");
      const token = await getAccessToken();
      return getDocumentViewUrl(documentId, token);
    },
    enabled: isAuthenticated && !!documentId,
    staleTime: 1800000, // 30 minutes (URLs expire after 1 hour)
    gcTime: 1800000, // Keep in cache for 30 minutes
  });
}

/**
 * Hook to download a document
 */
export function useDownloadDocument() {
  return useMutation({
    mutationFn: async ({ documentId, fileName }: { documentId: string; fileName: string }) => {
      const token = await getAccessToken();
      const blob = await downloadDocument(documentId, token);

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return { success: true };
    },
  });
}

/**
 * Hook to poll a specific document for status updates
 */
export function useDocumentPolling(documentId: string | undefined, enabled: boolean = true) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ["document", documentId],
    queryFn: async () => {
      if (!documentId) throw new Error("Document ID is required");
      const token = await getAccessToken();
      return getDocumentById(documentId, token);
    },
    enabled: isAuthenticated && !!documentId && enabled,
    refetchInterval: (query) => {
      const data = query.state.data as Document | undefined;
      // Stop polling if document has reached a terminal state
      if (
        data?.status === "completed" ||
        data?.status === "error" ||
        data?.status === "review_required"
      ) {
        return false;
      }
      // Poll every 2 seconds while processing
      return 2000;
    },
  });
}

/**
 * Hook to delete a document
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentId,
      hardDelete = false,
    }: {
      documentId: string;
      hardDelete?: boolean;
    }) => {
      const token = await getAccessToken();
      return deleteDocument(documentId, token, hardDelete);
    },
    onSuccess: (_data, { documentId }) => {
      // Remove from cache
      queryClient.setQueryData<Document[]>(["documents"], (old) => {
        if (!old) return [];
        return old.filter((doc) => doc.id !== documentId);
      });
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["document", documentId] });
    },
  });
}
